import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { Map } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _partial from 'lodash/partial'
import { getBoundActions } from '../../actions/index'
import Link from '../Link/index'
import { connect as connectStaticText } from '../StaticText/connect'
import BannerPlaceholder from '../BannerPlaceholder/index'
import {
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_COURSE,
  TYPE_CONTENT_VIDEO,
} from '../../services/content-type/index'
import {
  BANNER, upstreamContextOnClick as upstreamContextClick,
} from '../../services/upstream-context/index'
import HeroImage from '../HeroImage/index'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from '../Picture/index'
import SpotlightMetadata from '../SpotlightMetadata/index'
import SpotlightActionButtons from '../SpotlightActionButtons/index'
import {
  handleClick,
  getOptimizedSpotlightImages,
} from '../Spotlight/utils'

/**
 * Build class names for the spotlight container
 * @param {String} className - The class names being passed in
 * @param {Boolean} isPublished - Whether or not the content is published
 * @param {Boolean} isSeries - Whether or not the content type is series
 * @param {Boolean} publishedAtDateInFuture - Whether or not the published at date is in the future
 * @returns {String} All of the class names for the spotlight container
 */
function getClassName ({
  className,
  isPublished,
  isSeries,
  publishedAtDateInFuture,
  displayType,
  isCourse,
}) {
  const classArray = ['spotlight-banner-v2']
  if (isPublished && !publishedAtDateInFuture) {
    classArray.push('spotlight-banner-v2--published')
  }

  if (isSeries) {
    classArray.push('spotlight-banner-v2--series')
  }

  if (isCourse) {
    classArray.push('spotlight-banner-v2--course')
  }

  if (displayType) {
    classArray.push(`spotlight-banner-v2--${displayType}`)
  }

  return classArray.concat(className || []).join(' ')
}

/**
 * The static spotlight banner
 * @description The static spotlight banner that appears at the top of Member Home
 * @returns {JSX.Element} The static spotlight banner
 */
class SpotlightBannerV2 extends Component {
  onBannerContainerClick = (evt, upstreamContextOnClick) => {
    handleClick(null, this.props, 'Banner Event', upstreamContextOnClick)
  }

  getUpstreamContextOnClick = () => {
    const { props } = this
    const {
      upstreamContext,
      setUpstreamContext,
    } = props

    return _partial(
      upstreamContextClick,
      _partial.placeholder,
      { upstreamContext, setUpstreamContext },
    )
  }

  render () {
    const {
      props,
      onBannerContainerClick,
      getUpstreamContextOnClick,
    } = this
    const {
      data,
      className,
      publishedAtDateInFuture,
      viewportWidth,
      contentType,
    } = props
    const isPublished = data.get('published')
    const upstreamContextOnClick = getUpstreamContextOnClick()
    const isSeries = contentType === TYPE_CONTENT_SERIES
    const isCourse = contentType === TYPE_CONTENT_COURSE
    const isVideo = contentType === TYPE_CONTENT_VIDEO
    const displayType = data.get('displayType')
    const handleOnClick = isPublished && !publishedAtDateInFuture ? _partial(
      onBannerContainerClick,
      _partial.placeholder,
      upstreamContextOnClick,
    ) : () => {}
    const primaryCourseReference = data.get('primaryCourseReference')
    const url = isVideo && primaryCourseReference && primaryCourseReference.size > 0
      ? data.getIn(['primaryCourseReference', 'contentTo', 0, 'path'])
      : data.get('url')
    const heroImage = data.get('heroImage')
    const image = isCourse ? data.get('posterImage') : data.get('largeKeyArtImage')
    const imageUrls = getOptimizedSpotlightImages({ url: image }) || {}
    const {
      fallbackImageUrl,
      smallImageUrl,
      mediumImageUrl,
    } = imageUrls

    if (!Map.isMap(data) || !data.size) {
      return <BannerPlaceholder />
    }

    return (
      <div
        data-element={BANNER}
        className={getClassName({
          className,
          isPublished,
          isSeries,
          publishedAtDateInFuture,
          displayType,
          isCourse,
        })}
      >
        {
          viewportWidth <= 767
            ? <Link
                to={url}
                onClick={(e) => handleOnClick(e)}
                className="spotlight-banner-v2__mobile-hero-wrapper"
              >
                <Picture
                  fallbackSrc={fallbackImageUrl}
                  width={'608'}
                  height={'342'}
                  alt={data.get('title')}
                  className="spotlight-banner-v2__mobile-hero"
                  imageClassName="spotlight-banner-v2__mobile-hero-img"
                  loading={IMAGE_LOADING_LAZY}
                  sources={[
                    {
                      type: IMAGE_TYPE_WEBP,
                      srcSet: smallImageUrl,
                      media: '(max-width: 480px)',
                    },
                    {
                      type: IMAGE_TYPE_WEBP,
                      srcSet: mediumImageUrl,
                      media: '(max-width: 768px)',
                    },
                  ]}
                />
                <div className="spotlight-banner-v2__overlay-darken" />
              </Link>
            : <HeroImage
                className={['spotlight-banner-v2__background-hero']}
                alwaysUseProps
                smallUrl={heroImage.get('small')}
                mediumSmallUrl={heroImage.get('mediumSmall')}
                mediumUrl={heroImage.get('medium')}
                largeUrl={heroImage.get('large')}
                url={heroImage.get('large')}
              />
        }
        <div className="spotlight-banner-v2__content">
          <SpotlightMetadata {...props} upstreamContextOnClick={upstreamContextOnClick} />
          <SpotlightActionButtons {...props} upstreamContextOnClick={upstreamContextOnClick} />
        </div>
        <Link
          to={url}
          onClick={(e) => handleOnClick(e)}
          className="spotlight-banner-v2__overlay"
        />
        <div className="spotlight-banner-v2__overlay-darken" />
      </div>
    )
  }
}

SpotlightBannerV2.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  className: PropTypes.array,
  data: ImmutablePropTypes.map.isRequired,
  mediaId: PropTypes.number,
  label: PropTypes.string,
  language: PropTypes.string,
  upstreamContext: ImmutablePropTypes.map,
  setUpstreamContext: PropTypes.func,
  setEventDefault: PropTypes.func,
  contentType: PropTypes.string,
  payloadType: PropTypes.string,
  viewportWidth: PropTypes.number,
  useGraphQLData: PropTypes.bool,
  section: ImmutablePropTypes.map,
}

export default compose(
  connectRedux(
    (state, props) => {
      const { data } = props
      const today = new Date()
      const publishedAtDate = new Date(data.get('created'))
      const publishedAtDateInFuture = publishedAtDate > today

      return {
        auth: state.auth,
        staticText: state.staticText.getIn(['data', 'spotlightMetadata', 'data']),
        publishedAtDateInFuture,
        viewportWidth: state.app.getIn(['viewport', 'width'], 0),
        contentType: data.get('contentType'),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setUpstreamContext: actions.upstreamContext.setUpstreamContext,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
  connectStaticText({ storeKey: 'spotlightBanner' }),
)(SpotlightBannerV2)
